import { useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type { FaqCategory } from '../types';
import { apiService } from '../services/api';
import { SortableCategoryList } from './SortableCategoryList';
import { SortableCategoryItem } from './SortableCategoryItem';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  categories: FaqCategory[];
  onCategoriesChange: () => void;
}

interface EditState {
  id: string;
  name: string;
}

export const CategoryManagementModal: React.FC<Props> = ({
  isOpen,
  onClose,
  categories,
  onCategoriesChange,
}) => {
  // 製品カテゴリ追加フォーム
  const [newParentName, setNewParentName] = useState('');

  // FAQカテゴリ追加フォーム
  const [newFaqProductCategoryId, setNewFaqProductCategoryId] = useState('');
  const [newFaqCategoryName, setNewFaqCategoryName] = useState('');

  // 展開状態
  const [expandedParentIds, setExpandedParentIds] = useState<Set<string>>(new Set());

  // 編集状態
  const [editing, setEditing] = useState<EditState | null>(null);

  // ローディング・エラー
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const productCategories = categories
    .filter((c) => !c.productCategoryId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const faqCategories = categories.filter((c) => Boolean(c.productCategoryId));

  const getChildrenOf = (parentId: string) =>
    faqCategories
      .filter((c) => c.productCategoryId === parentId)
      .sort((a, b) => a.sortOrder - b.sortOrder);

  const toggleExpand = (id: string) => {
    setExpandedParentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAddProductCategory = async () => {
    if (!newParentName.trim()) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await apiService.createCategory({
        id: `cat-${crypto.randomUUID()}`,
        name: newParentName.trim(),
      });
      setNewParentName('');
      onCategoriesChange();
    } catch {
      setErrorMessage('製品カテゴリの追加に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddFaqCategory = async () => {
    if (!newFaqProductCategoryId || !newFaqCategoryName.trim()) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await apiService.createCategory({
        id: `sub-${crypto.randomUUID()}`,
        name: newFaqCategoryName.trim(),
        productCategoryId: newFaqProductCategoryId,
      });
      setNewFaqCategoryName('');
      setNewFaqProductCategoryId('');
      onCategoriesChange();
    } catch {
      setErrorMessage('FAQカテゴリの追加に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = (cat: FaqCategory) => {
    setEditing({ id: cat.id, name: cat.name });
    setErrorMessage(null);
  };

  const handleCancelEdit = () => {
    setEditing(null);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await apiService.updateCategory(editing.id, {
        name: editing.name.trim(),
        sortOrder: categories.find((c) => c.id === editing.id)?.sortOrder ?? 0,
      });
      setEditing(null);
      onCategoriesChange();
    } catch {
      setErrorMessage('カテゴリの更新に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (cat: FaqCategory) => {
    if (!window.confirm(`「${cat.name}」を削除してもよいですか？`)) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await apiService.deleteCategory(cat.id);
      onCategoriesChange();
    } catch {
      setErrorMessage('削除に失敗しました。紐づく FAQ やFAQカテゴリが存在する可能性があります。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReorderProductCategories = async (activeId: string, overId: string) => {
    const oldIndex = productCategories.findIndex((c) => c.id === activeId);
    const newIndex = productCategories.findIndex((c) => c.id === overId);
    const reordered = arrayMove(productCategories, oldIndex, newIndex);
    const items = reordered.map((c, i) => ({ id: c.id, sortOrder: i }));
    try {
      await apiService.reorderCategories(items);
      onCategoriesChange();
    } catch {
      setErrorMessage('並び替えに失敗しました');
      onCategoriesChange();
    }
  };

  const handleReorderFaqCategories = async (parentId: string, activeId: string, overId: string) => {
    const children = getChildrenOf(parentId);
    const oldIndex = children.findIndex((c) => c.id === activeId);
    const newIndex = children.findIndex((c) => c.id === overId);
    const reordered = arrayMove(children, oldIndex, newIndex);
    const items = reordered.map((c, i) => ({ id: c.id, sortOrder: i }));
    try {
      await apiService.reorderCategories(items);
      onCategoriesChange();
    } catch {
      setErrorMessage('並び替えに失敗しました');
      onCategoriesChange();
    }
  };

  if (!isOpen) return null;

  const renderEditRow = (cat: FaqCategory, isChild: boolean) => (
    <tr key={cat.id} className={isChild ? 'bg-slate-50' : ''}>
      <td className="w-8 px-2 py-2" />
      <td className="px-4 py-2 text-sm text-slate-900">
        {isChild && <span className="mr-2 text-slate-400">└</span>}
        <input
          type="text"
          value={editing!.name}
          onChange={(e) => setEditing({ ...editing!, name: e.target.value })}
          className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </td>
      <td className="px-4 py-2 text-center">
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={handleSaveEdit}
            disabled={isSubmitting}
            className="rounded px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            保存
          </button>
          <button
            onClick={handleCancelEdit}
            className="rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
          >
            キャンセル
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">カテゴリ管理</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <p className="mb-4 text-xs text-slate-400">
          ここでの並び順がチャット画面のカテゴリ表示順に反映されます
        </p>

        {errorMessage && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{errorMessage}</div>
        )}

        {/* 製品カテゴリ追加フォーム */}
        <div className="mb-4 rounded-lg border border-slate-200 p-3">
          <h3 className="mb-2 text-sm font-semibold text-slate-700">製品カテゴリを追加</h3>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-xs text-slate-500">名前</label>
              <input
                type="text"
                value={newParentName}
                onChange={(e) => setNewParentName(e.target.value)}
                placeholder="カテゴリ名"
                className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleAddProductCategory}
              disabled={isSubmitting || !newParentName.trim()}
              className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              追加
            </button>
          </div>
        </div>

        {/* FAQカテゴリ追加フォーム */}
        <div className="mb-4 rounded-lg border border-slate-200 p-3">
          <h3 className="mb-2 text-sm font-semibold text-slate-700">FAQカテゴリを追加</h3>
          <div className="flex items-end gap-2">
            <div className="w-40">
              <label className="mb-1 block text-xs text-slate-500">製品カテゴリ</label>
              <select
                value={newFaqProductCategoryId}
                onChange={(e) => setNewFaqProductCategoryId(e.target.value)}
                className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">選択...</option>
                {productCategories.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs text-slate-500">名前</label>
              <input
                type="text"
                value={newFaqCategoryName}
                onChange={(e) => setNewFaqCategoryName(e.target.value)}
                placeholder="FAQカテゴリ名"
                className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleAddFaqCategory}
              disabled={isSubmitting || !newFaqProductCategoryId || !newFaqCategoryName.trim()}
              className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              追加
            </button>
          </div>
        </div>

        {/* カテゴリ一覧テーブル */}
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-8 px-2 py-2"></th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">名前</th>
                <th className="w-40 px-4 py-2 text-center text-xs font-medium text-slate-500">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {productCategories.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-slate-500">
                    カテゴリがありません
                  </td>
                </tr>
              ) : (
                <SortableCategoryList
                  items={productCategories}
                  onReorder={handleReorderProductCategories}
                >
                  {productCategories.flatMap((parent) => {
                    const isEditingParent = editing?.id === parent.id;
                    const rows: React.ReactNode[] = [
                      isEditingParent ? (
                        renderEditRow(parent, false)
                      ) : (
                        <SortableCategoryItem
                          key={parent.id}
                          category={parent}
                          isChild={false}
                          isExpanded={expandedParentIds.has(parent.id)}
                          isSubmitting={isSubmitting}
                          onToggleExpand={() => toggleExpand(parent.id)}
                          onStartEdit={() => handleStartEdit(parent)}
                          onDelete={() => handleDelete(parent)}
                        />
                      ),
                    ];
                    if (expandedParentIds.has(parent.id)) {
                      const children = getChildrenOf(parent.id);
                      if (children.length > 0) {
                        rows.push(
                          <SortableCategoryList
                            key={`${parent.id}-children`}
                            items={children}
                            onReorder={(activeId, overId) =>
                              handleReorderFaqCategories(parent.id, activeId, overId)
                            }
                          >
                            {children.map((child) =>
                              editing?.id === child.id ? (
                                renderEditRow(child, true)
                              ) : (
                                <SortableCategoryItem
                                  key={child.id}
                                  category={child}
                                  isChild={true}
                                  isSubmitting={isSubmitting}
                                  onStartEdit={() => handleStartEdit(child)}
                                  onDelete={() => handleDelete(child)}
                                />
                              ),
                            )}
                          </SortableCategoryList>,
                        );
                      } else {
                        rows.push(
                          <tr key={`${parent.id}-empty`} className="bg-slate-50">
                            <td
                              colSpan={3}
                              className="px-4 py-2 text-center text-xs text-slate-400"
                            >
                              FAQカテゴリなし
                            </td>
                          </tr>,
                        );
                      }
                    }
                    return rows;
                  })}
                </SortableCategoryList>
              )}
            </tbody>
          </table>
        </div>

        {/* 閉じるボタン */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};
